import React from "react";
import { Space } from "antd";
import { bold, boldAndItalic } from "utils";
import { AICC_PROMPT_GROUP } from "constants/aiccPrompt";
import { aiccPromptPath } from "features/aicc/Prompt/paths";
import { AICC_ASSET_TYPE, AiccAssetType } from "constants/aiccLanguage";

/**
 * AICC2-335 §3 AC-2, verbatim, including the typed confirmation. Soft delete, so the
 * organization dialog's "This can not be undone." would be wrong here.
 */
export const DELETE_LANGUAGE_TITLE = "Confirm";

export const DELETE_LANGUAGE_OK_TEXT = "Confirm Delete";

/** What the admin has to type before Confirm Delete becomes usable. */
export const DELETE_LANGUAGE_CONFIRMATION = "delete";

export const renderLanguageDeletionMessage = () => (
  <Space direction="vertical" size={12}>
    {
      "Are you sure you want to delete this language? This action is a soft delete—all associated data and historical records will be safely preserved in the database, but the language will be removed from this list."
    }
    {boldAndItalic`Type ${DELETE_LANGUAGE_CONFIRMATION} to continue`}
  </Space>
);

/**
 * AICC2-331 AC-3 "Editing Single asset when publish-ready". The language list no longer edits
 * assets — each sub-asset screen does — so these are consumed there (see
 * LearningAssets/DefaultScenario/confirmations.tsx). Any edit releases the publish-ready
 * lock, server-side and on every write path, which is what the dialog warns about.
 */
export const REVERT_ON_EDIT_TITLE = "Edit Publish-Ready Asset?";

export const REVERT_ON_EDIT_MESSAGE =
  "Are you sure you want to proceed with this edit? Once saved, you will need to re-test the updated version before making it publish-ready again.";

export const REVERT_ON_EDIT_OK_TEXT = "Save & Revert to Preview";

/**
 * Exported because resetting a prompt's text is an edit too (AICC2-328): it releases the same server-side
 * lock, so it owes the same warning, in the same words. The sentence lives here and nowhere else.
 */
export const renderRevertOnEditWarning = () =>
  bold`⚠️ ${"Status Change Warning"}: Saving changes will drop this asset's status from ${"Publish-Ready"} back to ${"Preview-Ready"}.`;

export const renderRevertOnEditContent = () => (
  <Space direction="vertical" size={12}>
    {renderRevertOnEditWarning()}
    {REVERT_ON_EDIT_MESSAGE}
  </Space>
);

/**
 * Where a sub-asset is edited. The rest of the fourteen cannot be opened yet, which is a different
 * reason for a disabled Manage than the content being frozen.
 *
 * The four prompt assets all open the same screen, each on its own tab — which is why the tab is part
 * of the path rather than something handed over in the store: the button only has to name a URL.
 */
export const ASSET_SCREEN_PATH: Partial<Record<AiccAssetType, string>> = {
  [AICC_ASSET_TYPE.defaultScenarios]: "/aicc/learning-assets/default-scenarios",
  [AICC_ASSET_TYPE.scenarioCategories]: "/aicc/learning-assets/scenario-categories",
  [AICC_ASSET_TYPE.lessonInformation]: "/aicc/learning-assets/lesson-information",
  [AICC_ASSET_TYPE.vocabulary]: "/aicc/learning-assets/vocabularies",
  [AICC_ASSET_TYPE.canDoStatement]: "/aicc/learning-assets/can-do-statements",
  [AICC_ASSET_TYPE.lessonReviewScript]: "/aicc/learning-assets/lesson-review-scripts",
  [AICC_ASSET_TYPE.sttSetting]: "/aicc/learning-assets/stt-settings",
  [AICC_ASSET_TYPE.ttsSetting]: "/aicc/learning-assets/tts-settings",
  [AICC_ASSET_TYPE.uiElementTranslations]: "/aicc/learning-assets/ui-element-translations",
  [AICC_ASSET_TYPE.onboardingPrompt]: aiccPromptPath(AICC_PROMPT_GROUP.onboarding),
  [AICC_ASSET_TYPE.scenarioConversationPrompt]: aiccPromptPath(AICC_PROMPT_GROUP.scenarioConversation),
  [AICC_ASSET_TYPE.lessonReviewPrompt]: aiccPromptPath(AICC_PROMPT_GROUP.lessonReview),
  [AICC_ASSET_TYPE.feedbackAssessmentPrompt]: aiccPromptPath(AICC_PROMPT_GROUP.supportingFunctions),
};

export const MANAGE_NO_SCREEN_HINT = "This sub-asset does not have a management screen yet.";

/** AICC2-331 AC-3: staging changes nothing for end users, only for test accounts. */
export const READY_TO_PUBLISH_TITLE = "Asset Status Update";

export const READY_TO_PUBLISH_MESSAGE =
  "Are you sure you want to stage this language asset for publishing? It will remain visible only to test accounts.";

/**
 * AICC2-331 AC-1, which is the corrected model: the copy is a branch, not a withdrawal. The
 * live version is untouched and keeps serving standard users while the copy is revised.
 */
export const DUPLICATE_PREVIEW_TITLE = "Create Preview from Published Version?";

export const DUPLICATE_PREVIEW_OK_TEXT = "Create Preview Version";

/** Says what it does not do, which is the part an admin would otherwise assume. */
export const DUPLICATE_PREVIEW_HINT =
  "Copies the live version into an editable preview that only test accounts see. The live version stays public.";

export const renderDuplicatePreviewContent = () => (
  <Space direction="vertical" size={12}>
    {bold`This will copy the current live ${"Published version"} to create a new ${"Preview-Ready"} draft.`}
    {"The active live version will remain unaffected."}
  </Space>
);

/**
 * The same action against a language that is already being revised: AICC2-331 AC-5 words it
 * "Duplicate Preview (V1 Overwrite V2)". The copy in progress is discarded, so this dialog is the
 * only warning an admin gets before losing unpublished edits — the backend answers 409 rather than
 * overwriting on its own.
 */
export const DUPLICATE_PREVIEW_OVERWRITE_TITLE = "Overwrite the Preview in Progress?";

export const DUPLICATE_PREVIEW_OVERWRITE_OK_TEXT = "Overwrite Preview Version";

export const renderDuplicatePreviewOverwriteContent = (liveVersionNumber: number, workingVersionNumber: number) => (
  <Space direction="vertical" size={12}>
    {bold`⚠️ V${String(workingVersionNumber)} is already in progress. Copying V${String(
      liveVersionNumber
    )} again will ${"discard"} it, including any unpublished edits.`}
    {"The live version stays public either way, and the new copy starts as Preview-Ready."}
  </Space>
);

/**
 * The one read-only state whose way out is not written on the row itself: a withdrawn version's
 * sub-assets cannot be edited, and Duplicate Preview is no help because there is no live version
 * left to clone. Restoring a version is what creates something editable.
 */
export const WITHDRAWN_ASSETS_NOTE =
  "Sub-assets are read-only while this language is withdrawn. Restore a version from the history to edit it again.";

/**
 * Shown while an opened card reads its versions. Worded as what is being fetched rather than as
 * "loading", because the wait is a couple of seconds and belongs to this card alone — the rest of the
 * list stays usable while it runs.
 */
export const CONTENT_VERSIONS_LOADING = "Loading versions and sub-assets";

/**
 * Shown on one card while that language is re-read after one of its own actions. Says "updating"
 * rather than "loading": the card already holds an answer, and what is happening is that it is being
 * replaced by a newer one. Its own actions are held until it lands, so the label is also what
 * explains the buttons being briefly unavailable.
 */
export const LANGUAGE_REFRESHING = "Updating";

/** AICC2-331 AC-4. Two shapes, because the first publish replaces nothing. */
export const publishTitle = (languageName: string, liveVersionNumber?: number) =>
  liveVersionNumber ? `Overwrite Live ${languageName} AICC Version?` : `Public ${languageName} AICC Function?`;

export const publishOkText = (liveVersionNumber?: number) =>
  liveVersionNumber ? "Overwrite Live" : "Public to All User";

export const renderPublishIntro = (languageName: string, liveVersionNumber: number | undefined, nextVersion: number) =>
  liveVersionNumber ? (
    <Space direction="vertical" size={12}>
      {bold`${"Current Live"}: V${String(liveVersionNumber)} Public ➔ ${"Will be replaced by"}: V${String(
        nextVersion
      )} Publish-Ready`}
      {
        "Are you sure you want to deploy this version to Public? This will immediately replace the current live version and apply to all active users."
      }
    </Space>
  ) : (
    <Space direction="vertical" size={12}>
      {`This action will make the ${languageName} AICC function visible to all public users.`}
    </Space>
  );

/**
 * AICC2-331 §6 AC-2, verbatim. Restore does not go live: it stages the old content as a
 * working copy, overwriting whatever copy was in progress.
 */
export const RESTORE_VERSION_TITLE = "Restore Version";

export const RESTORE_VERSION_OK_TEXT = "Confirm Restore";

export const renderRestoreVersionMessage = (version: number) => (
  <Space direction="vertical" size={12}>
    {bold`Are you sure you want to restore ${`V${version}`}? This action will overwrite your current ${"preview-ready"} or ${"publish-ready"} working assets (if one exists).`}
    {
      "The restored version will be staged as preview-ready for testing and will NOT affect the live environment until you explicitly publish it."
    }
  </Space>
);
